import React, { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { v4API } from '../../Api';
import { actionKey as mutationKey, dateTime, errorMessage } from './common';
import TelegramBulkActions from './TelegramBulkActions';

const emptyPage = { items: [], total: 0 };
const scalar = value => typeof value === 'string' || typeof value === 'number' ? String(value) : '—';
const rub = value => typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value) ? `${value} RUB` : '—';
const validAmount = value => /^-?(0|[1-9]\d*)\.\d{2}$/.test(value) && !/^-?0\.00$/.test(value);
const pageData = data => ({ items: Array.isArray(data?.items) ? data.items : [], total: Number.isSafeInteger(data?.total) && data.total >= 0 ? data.total : 0 });
function requestKey(ref, body) {
  const fingerprint = JSON.stringify(body);
  if (ref.current?.fingerprint !== fingerprint) ref.current = { fingerprint, key: mutationKey() };
  return ref.current.key;
}
function Pagination({ result, page, setPage, loading }) {
  return <TablePagination component="div" count={result.total} page={page - 1} rowsPerPage={25} rowsPerPageOptions={[25]}
    labelRowsPerPage="На странице" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
    onPageChange={(_, next) => setPage(next + 1)} disabled={loading} />;
}

function useMutationNavigation(busy) {
  const navigation = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    if (!busy) return undefined;
    const unload = event => { event.preventDefault(); event.returnValue = ''; };
    const click = event => { if (event.target.closest?.('a[href]')) { event.preventDefault(); event.stopPropagation(); } };
    const index = window.history.state?.idx;
    const pop = event => {
      const next = event.state?.idx;
      if (Number.isInteger(index) && Number.isInteger(next) && next !== index) {
        event.stopImmediatePropagation();
        window.history.go(index - next);
      }
    };
    const navigator = navigation?.navigator;
    const methods = navigator ? Object.fromEntries(['push', 'replace', 'go'].map(name => [name, navigator[name]])) : {};
    Object.keys(methods).forEach(name => { navigator[name] = () => {}; });
    window.addEventListener('beforeunload', unload);
    window.addEventListener('popstate', pop, true);
    document.addEventListener('click', click, true);
    return () => {
      Object.entries(methods).forEach(([name, method]) => { navigator[name] = method; });
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('popstate', pop, true);
      document.removeEventListener('click', click, true);
    };
  }, [busy, navigation]);
}
function storedMutation(userId) {
  try { return JSON.parse(sessionStorage.getItem(`v4-telegram-user-${userId}`)) || null; } catch { return null; }
}
function UserDetail({ userId, onChanged, onClose, bulkLocked }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(() => storedMutation(userId));
  const uncertainRef = useRef(uncertain);
  useMutationNavigation(busy);
  const [metadata, setMetadata] = useState({ banned: false, orders: '0', comment: '', reason: '' });
  const [balance, setBalance] = useState({ amount: '', reason: '' });
  const [confirmation, setConfirmation] = useState(null);
  const [history, setHistory] = useState(emptyPage);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyReload, setHistoryReload] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const metadataKey = useRef(null);
  const balanceKey = useRef(null);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    v4API.telegramUser(userId).then(data => {
      if (!active) return;
      setUser(data);
      setMetadata({ banned: data.banned === true, orders: String(data.orders), comment: data.comment || '', reason: '' });
      setStale(false);
    }).catch(err => { if (active) { setError(errorMessage(err)); setUser(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, reload]);
  useEffect(() => {
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    v4API.telegramUserOperations(userId, { page: historyPage, limit: 25 }).then(data => {
      if (active) setHistory(pageData(data));
    }).catch(err => { if (active) { setHistoryError(errorMessage(err)); setHistory(emptyPage); } })
      .finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
  }, [userId, historyPage, historyReload]);

  const blocked = busy || loading || stale || !user || bulkLocked;
  const fieldsBlocked = blocked || !!uncertain;
  const remember = value => {
    if (value) sessionStorage.setItem(`v4-telegram-user-${userId}`, JSON.stringify(value));
    else sessionStorage.removeItem(`v4-telegram-user-${userId}`);
    uncertainRef.current = value;
    setUncertain(value);
  };
  const metadataValid = /^(?:[0-9]|10)$/.test(metadata.orders) && !!metadata.reason.trim() && metadata.reason.length <= 1024 && metadata.comment.length <= 2048 && (user?.updatedAt === null || !!user?.updatedAt);
  const balanceValid = validAmount(balance.amount) && !!balance.reason.trim() && balance.reason.length <= 1024 && rub(user?.balance) !== '—' && user?.currency === 'RUB';
  const changeMetadata = (name, value) => {
    metadataKey.current = null;
    setMetadata(current => ({ ...current, [name]: value }));
    setNotice('');
  };
  const changeBalance = (name, value) => {
    balanceKey.current = null;
    setBalance(current => ({ ...current, [name]: value }));
    setConfirmation(null);
    setNotice('');
  };
  const mutate = async (kind, body) => {
    if (pending.current || blocked || (uncertainRef.current && (uncertainRef.current.kind !== kind || JSON.stringify(uncertainRef.current.body) !== JSON.stringify(body)))) return;
    const retrying = !!uncertainRef.current;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const key = uncertainRef.current?.key || requestKey(kind === 'balance' ? balanceKey : metadataKey, body);
      remember({ kind, body, key });
      const result = kind === 'balance'
        ? await v4API.adjustTelegramBalance(userId, body, key)
        : await v4API.updateTelegramUser(userId, body, key);
      if (!mounted.current) return;
      remember(null);
      const updated = kind === 'balance' ? result.user : result;
      setUser(updated);
      setMetadata({ banned: updated.banned === true, orders: String(updated.orders), comment: updated.comment || '', reason: '' });
      if (kind === 'balance') {
        setBalance({ amount: '', reason: '' });
        balanceKey.current = null;
        setConfirmation(null);
        setNotice(`Баланс V4 обновлён. Операция: ${scalar(result.operationId)}${result.replayed ? ' (повторный ответ, без повторного списания или зачисления)' : ''}.`);
      } else {
        metadataKey.current = null;
        setNotice('Данные пользователя V4 сохранены.');
      }
      setHistoryReload(value => value + 1);
      onChanged();
    } catch (err) {
      if (!mounted.current) return;
      if (!retrying && [400, 422].includes(err?.response?.status)) {
        remember(null);
        (kind === 'balance' ? balanceKey : metadataKey).current = null;
        setConfirmation(null);
        setError('Операция отклонена: проверьте данные и исправьте запрос. Причина — не более 1024 символов, комментарий — не более 2048.');
      } else if (err?.response?.status === 409 && !retrying) {
        remember(null);
        setStale(true);
        setConfirmation(null);
        setError('Конфликт данных или ключа операции. Обновите карточку и проверьте историю перед новым действием.');
      } else {
        setError(`${errorMessage(err)} Результат операции может быть неизвестен. Повтор неизменённого запроса использует тот же ключ; перед новым действием проверьте историю.`);
      }
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return <Dialog open fullWidth maxWidth="lg" scroll="paper" aria-labelledby="telegram-user-title" onClose={() => { if (!pending.current) onClose(); }} disableEscapeKeyDown={busy} PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }, maxHeight: 'calc(100% - 32px)' } }}>
    <DialogTitle id="telegram-user-title">Пользователь {userId}</DialogTitle>
    <DialogContent dividers><Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" gap={2}>
        <Button disabled={busy || loading} onClick={() => { setConfirmation(null); setReload(value => value + 1); setHistoryReload(value => value + 1); }}>Обновить карточку</Button>
      </Stack>
      {loading && <LinearProgress aria-label="Загрузка пользователя" />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      {bulkLocked && <Alert severity="warning">Изменения заблокированы до проверки или завершения массового задания V4. Просмотр доступен.</Alert>}
      {uncertain && <Alert severity="warning" action={<Button disabled={blocked} onClick={() => uncertain.kind === 'balance' ? setConfirmation(uncertain.body) : mutate(uncertain.kind, uncertain.body)}>Повторить неизменённую операцию</Button>}>Результат операции неизвестен. Исходный запрос и ключ сохранены даже при закрытии карточки. До получения ответа новые изменения недоступны. Проверьте историю и повторите неизменённую операцию.</Alert>}
      {user && <>
        <Typography>Баланс V4: {rub(user.balance)}</Typography>
        <Typography variant="body2">Создан: {dateTime(user.createdAt)} · Обновлён: {dateTime(user.updatedAt)} · Последняя активность: {dateTime(user.lastActivity)}</Typography>
        <Stack component="form" spacing={2} onSubmit={event => {
          event.preventDefault();
          if (metadataValid) mutate('metadata', { banned: metadata.banned, orders: Number(metadata.orders), comment: metadata.comment, reason: metadata.reason.trim(), expectedUpdatedAt: user.updatedAt });
        }}>
          <Typography variant="h6" component="h3">Данные пользователя</Typography>
          <FormControlLabel control={<Checkbox checked={metadata.banned} disabled={fieldsBlocked} onChange={event => changeMetadata('banned', event.target.checked)} />} label="Заблокирован" />
          <TextField label="Квота заказов (0–10)" value={metadata.orders} disabled={fieldsBlocked} onChange={event => changeMetadata('orders', event.target.value)} inputProps={{ inputMode: 'numeric' }} error={!/^(?:[0-9]|10)$/.test(metadata.orders)}
            helperText="Для рублёвых методов. Ежедневно в 03:00 МСК восстанавливается до 5. Крипта и покупки с баланса квоту не расходуют." />
          <TextField label="Комментарий" value={metadata.comment} inputProps={{ maxLength: 2048 }} helperText="Не более 2048 символов" error={metadata.comment.length > 2048} disabled={fieldsBlocked} onChange={event => changeMetadata('comment', event.target.value)} multiline />
          <TextField label="Причина изменения данных" value={metadata.reason} inputProps={{ maxLength: 1024 }} helperText="Не более 1024 символов" error={metadata.reason.length > 1024} disabled={fieldsBlocked} onChange={event => changeMetadata('reason', event.target.value)} required />
          <Button type="submit" variant="contained" disabled={fieldsBlocked || !metadataValid}>Сохранить данные</Button>
        </Stack>
        <Stack component="form" spacing={2} onSubmit={event => {
          event.preventDefault();
          if (!blocked && balanceValid) setConfirmation({ amount: balance.amount, expectedBalance: user.balance, reason: balance.reason.trim() });
        }}>
          <Typography variant="h6" component="h3">Корректировка Telegram-баланса V4</Typography>
          <Alert severity="warning">Изменяется только Telegram-баланс V4, общий для мерчантов в режиме telegram и изолированный от legacy. Старые балансы не затрагиваются. Положительная сумма зачисляет, отрицательная списывает RUB.</Alert>
          <TextField label="Изменение баланса (RUB)" value={balance.amount} disabled={fieldsBlocked} onChange={event => changeBalance('amount', event.target.value)}
            helperText="Например: 10.00 или -10.00. Ровно два знака после точки; сумма не может быть нулевой." error={!!balance.amount && !validAmount(balance.amount)} />
          <TextField label="Причина изменения баланса" value={balance.reason} inputProps={{ maxLength: 1024 }} helperText="Не более 1024 символов" error={balance.reason.length > 1024} disabled={fieldsBlocked} onChange={event => changeBalance('reason', event.target.value)} required />
          <Button type="submit" color="warning" variant="outlined" disabled={fieldsBlocked || !balanceValid}>Проверить корректировку</Button>
        </Stack>
      </>}
      <Typography variant="h6" component="h3">История операций V4</Typography>
      <Alert severity="info">Здесь только история V4. Старый общий аудит legacy не смешивается с этими операциями.</Alert>
      <Button disabled={historyLoading || busy} onClick={() => setHistoryReload(value => value + 1)}>Обновить историю</Button>
      {historyLoading && <LinearProgress aria-label="Загрузка истории" />}
      {historyError && <Alert severity="error">{historyError}</Alert>}
      <TableContainer><Table size="small" aria-label="История операций" sx={{ minWidth: 1000 }}>
        <TableHead><TableRow>{['Операция / дата', 'Тип', 'Сумма', 'Баланс до', 'Баланс после', 'Мерчант', 'Инициатор', 'Причина', 'Заказ'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {!historyLoading && !history.items.length && <TableRow><TableCell colSpan={9}>Операций нет</TableCell></TableRow>}
          {history.items.map((row, index) => <TableRow key={`${scalar(row.operationId)}-${index}`}>
            <TableCell>{scalar(row.operationId)}<Typography variant="caption" component="div">{dateTime(row.createdAt)}</Typography></TableCell>
            <TableCell>{scalar(row.type)}</TableCell><TableCell>{rub(row.amount)}</TableCell><TableCell>{rub(row.balanceBefore)}</TableCell><TableCell>{rub(row.balanceAfter)}</TableCell>
            <TableCell>{scalar(row.merchantId)}</TableCell><TableCell>{typeof row.actor === 'object' && row.actor ? JSON.stringify(row.actor) : scalar(row.actor)}</TableCell><TableCell>{scalar(row.reason)}</TableCell><TableCell>{scalar(row.orderId)}</TableCell>
          </TableRow>)}
        </TableBody>
      </Table></TableContainer>
      <Pagination result={history} page={historyPage} setPage={setHistoryPage} loading={historyLoading} />
    </Stack></DialogContent>
    <DialogActions><Button disabled={busy} onClick={() => { if (!pending.current) onClose(); }}>Закрыть карточку</Button></DialogActions>
    <Dialog open={!!confirmation} onClose={() => { if (!busy) setConfirmation(null); }} fullWidth maxWidth="sm">
      <DialogTitle>Подтвердить изменение баланса V4</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Alert severity="warning">Изменится только Telegram-баланс V4. Legacy не изменяется. Это не тестовая операция; баланс V4 общий для мерчантов, а не отдельный кошелёк мерчанта.</Alert>
        <Typography>Telegram ID: {userId}</Typography>
        <Typography>Ожидаемый баланс: {rub(confirmation?.expectedBalance)}</Typography>
        <Typography>Изменение: {rub(confirmation?.amount)}</Typography>
        <Typography>Причина: {confirmation?.reason}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setConfirmation(null)}>Закрыть</Button><Button color="warning" variant="contained" disabled={blocked} onClick={() => mutate('balance', confirmation)}>Подтвердить изменение баланса</Button></DialogActions>
    </Dialog>
  </Dialog>;
}

export default function TelegramUsers() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ page: 1, limit: 25 });
  const [result, setResult] = useState(emptyPage);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState('');
  const [bulkLocked, setBulkLocked] = useState(true);
  useMutationNavigation(creating);
  const createPending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    v4API.telegramUsers(query).then(data => { if (active) setResult(pageData(data)); })
      .catch(err => { if (active) { setError(errorMessage(err)); setResult(emptyPage); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, reload]);
  const create = async event => {
    event.preventDefault();
    if (createPending.current || bulkLocked || !/^[1-9]\d*$/.test(userId.trim())) return;
    createPending.current = true;
    setCreating(true);
    setCreateError('');
    try {
      const user = await v4API.createTelegramUser({ userId: userId.trim() });
      if (!mounted.current) return;
      setSelected(String(user.userId));
      setUserId('');
      setReload(value => value + 1);
    } catch (err) { if (mounted.current) setCreateError(errorMessage(err)); }
    finally { createPending.current = false; if (mounted.current) setCreating(false); }
  };
  return <Stack spacing={3}>
    <Typography variant="h4" component="h2">Telegram-пользователи</Typography>
    <Alert severity="info">Telegram-пользователи и балансы V4 изолированы от legacy. Изменения на этой странице не затрагивают старые балансы. Telegram-баланс V4 общий для мерчантов в режиме telegram; изолированные кошельки V4 по мерчантам остаются отдельными.</Alert>
    <TelegramBulkActions disabled={creating || !!selected} onLockChange={setBulkLocked} onChanged={() => setReload(value => value + 1)} />
    <Paper component="form" sx={{ p: 2 }} onSubmit={event => {
      event.preventDefault();
      if (/^\d*$/.test(search.trim())) setQuery({ page: 1, limit: 25, ...(search.trim() ? { search: search.trim() } : {}) });
    }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField label="Поиск по Telegram ID" value={search} onChange={event => setSearch(event.target.value)} inputProps={{ inputMode: 'numeric' }} helperText="Полный ID или часть ID, только цифры" error={!/^\d*$/.test(search.trim())} />
      <Button type="submit" disabled={loading || !/^\d*$/.test(search.trim())}>Найти</Button>
      <Button disabled={loading} onClick={() => { setSearch(''); setQuery({ page: 1, limit: 25 }); }}>Сбросить</Button>
      <Button disabled={loading} onClick={() => setReload(value => value + 1)}>Обновить список</Button>
    </Stack></Paper>
    <Paper component="form" onSubmit={create} sx={{ p: 2 }}><Stack spacing={2}>
      <Typography variant="h6" component="h3">Создать пользователя V4</Typography>
      <TextField label="Telegram ID нового пользователя" value={userId} disabled={creating || bulkLocked} onChange={event => setUserId(event.target.value)} inputProps={{ inputMode: 'numeric' }} helperText="Положительный числовой Telegram ID" />
      <Button type="submit" variant="contained" disabled={creating || bulkLocked || !/^[1-9]\d*$/.test(userId.trim())}>Создать пользователя</Button>
      {createError && <Alert severity="error">{createError}</Alert>}
    </Stack></Paper>
    {error && <Alert severity="error">{error}</Alert>}
    <Paper>
      {loading && <LinearProgress aria-label="Загрузка Telegram-пользователей" />}
      <TableContainer><Table size="small" aria-label="Telegram-пользователи" sx={{ minWidth: 900 }}>
        <TableHead><TableRow>{['Telegram ID', 'Баланс V4', 'Статус', 'Квота заказов', 'Комментарий', 'Создан', 'Обновлён', 'Последняя активность'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {!loading && !result.items.length && <TableRow><TableCell colSpan={8}>Пользователи не найдены</TableCell></TableRow>}
          {result.items.map(user => <TableRow key={user.userId} selected={selected === String(user.userId)} hover>
            <TableCell><Button disabled={creating} onClick={() => setSelected(String(user.userId))}>{scalar(user.userId)}</Button></TableCell>
            <TableCell>{rub(user.balance)}</TableCell><TableCell>{user.banned ? 'Заблокирован' : 'Активен'}</TableCell><TableCell>{scalar(user.orders)}</TableCell><TableCell>{scalar(user.comment)}</TableCell>
            <TableCell>{dateTime(user.createdAt)}</TableCell><TableCell>{dateTime(user.updatedAt)}</TableCell><TableCell>{dateTime(user.lastActivity)}</TableCell>
          </TableRow>)}
        </TableBody>
      </Table></TableContainer>
      <Pagination result={result} page={query.page} setPage={page => setQuery(current => ({ ...current, page }))} loading={loading} />
    </Paper>
    {selected && <UserDetail key={selected} userId={selected} bulkLocked={bulkLocked} onClose={() => setSelected('')} onChanged={() => setReload(value => value + 1)} />}
  </Stack>;
}
